/**
 * Reusable utilities for converting MUI DataGrid filters to backend API filters.
 * Designed for DRY usage across different entities/tables in the Support app.
 */

import type { GridFilterItem, GridSortModel } from '@mui/x-data-grid'

// GridSortItem is not exported from @mui/x-data-grid, so we extract it from GridSortModel
type GridSortItem = GridSortModel[number]

/**
 * Backend text filter operators. Order matters: the first is the default every text column resets
 * to, and `startsWith` is the only one that can use an index prefix.
 */
export const TEXT_FILTER_OPERATORS = [
  'startsWith',
  'contains',
  'doesNotContain',
  'equals',
  'doesNotEqual',
  // 'endsWith', — backend supports it, but nothing asks for it.
  'isEmpty',
  'isNotEmpty',
  'isAnyOf'
] as const
export type TextFilterOperator = (typeof TEXT_FILTER_OPERATORS)[number]

/**
 * Full set of backend-supported text filter operators. Superset of the UI-exposed
 * `TextFilterOperator` — use this when hand-constructing filters outside the
 * DataGrid UI (e.g. dashboard search, deep links).
 */
export type BackendTextFilterOperator = TextFilterOperator | 'endsWith'

/**
 * Backend date filter operators
 */
export const DATE_FILTER_OPERATORS = [
  'is',
  'isNot',
  'isAfter',
  'isOnOrAfter',
  'isBefore',
  'isOnOrBefore',
  'isEmpty',
  'isNotEmpty'
] as const
export type DateFilterOperator = (typeof DATE_FILTER_OPERATORS)[number]

/**
 * Backend number filter operators
 */
export const NUMBER_FILTER_OPERATORS = [
  'equals',
  'doesNotEqual',
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
  'isEmpty',
  'isNotEmpty'
] as const
export type NumberFilterOperator = (typeof NUMBER_FILTER_OPERATORS)[number]

/**
 * Backend filter types
 */
export type TextFilter = {
  operator: TextFilterOperator
  value?: string
  values?: string[] // For 'isAnyOf' operator
}

export type DateFilter = {
  operator: DateFilterOperator
  value?: string // ISO date string
}

export type NumberFilter = {
  operator: NumberFilterOperator
  value?: string // number as string
}

/**
 * MUI DataGrid to backend operator mappings.
 * MUI uses different operator names in some cases.
 */
const MUI_TO_BACKEND_TEXT_OPERATORS: Record<string, TextFilterOperator> = {
  contains: 'contains',
  doesNotContain: 'doesNotContain',
  equals: 'equals',
  doesNotEqual: 'doesNotEqual',
  startsWith: 'startsWith',
  // endsWith: 'endsWith',
  isEmpty: 'isEmpty',
  isNotEmpty: 'isNotEmpty',
  isAnyOf: 'isAnyOf',

  // MUI uses these variants
  '=': 'equals',
  '!=': 'doesNotEqual'
}

const MUI_TO_BACKEND_DATE_OPERATORS: Record<string, DateFilterOperator> = {
  is: 'is',
  not: 'isNot',
  isNot: 'isNot',
  after: 'isAfter',
  isAfter: 'isAfter',
  onOrAfter: 'isOnOrAfter',
  isOnOrAfter: 'isOnOrAfter',
  before: 'isBefore',
  isBefore: 'isBefore',
  onOrBefore: 'isOnOrBefore',
  isOnOrBefore: 'isOnOrBefore',
  isEmpty: 'isEmpty',
  isNotEmpty: 'isNotEmpty'
}

const MUI_TO_BACKEND_NUMBER_OPERATORS: Record<string, NumberFilterOperator> = {
  equals: 'equals',
  doesNotEqual: 'doesNotEqual',
  greaterThan: 'greaterThan',
  greaterThanOrEqual: 'greaterThanOrEqual',
  lessThan: 'lessThan',
  lessThanOrEqual: 'lessThanOrEqual',
  isEmpty: 'isEmpty',
  isNotEmpty: 'isNotEmpty',

  //  MUI DataGrid sends symbolic operators (=, !=, >, <) for numeric columns by default
  '=': 'equals',
  '!=': 'doesNotEqual',
  '>': 'greaterThan',
  '>=': 'greaterThanOrEqual',
  '<': 'lessThan',
  '<=': 'lessThanOrEqual'
}

/**
 * Filter type for a field - determines how MUI operators are mapped
 */
export type FilterFieldType = 'text' | 'date' | 'boolean' | 'number'

/**
 * Configuration for mapping a DataGrid field to a backend filter parameter.
 * The key is the DataGrid field name, and the value describes how to map it.
 */
export type FilterFieldConfig = {
  /** The backend filter parameter name (e.g., 'patientFilter', 'emailFilter') */
  paramName: string
  /** The type of filter - determines operator mapping */
  type: FilterFieldType
}

export type FilterFieldMapping = Record<string, FilterFieldConfig>

/**
 * Configuration for mapping DataGrid sort fields to backend sort parameters.
 */
export type SortFieldMapping = Record<string, string>

/**
 * Converts a single MUI DataGrid filter item to a backend filter object.
 */
function convertTextFilter(item: GridFilterItem): TextFilter | null {
  const operator = MUI_TO_BACKEND_TEXT_OPERATORS[item.operator]
  if (!operator) return null

  // isEmpty/isNotEmpty don't need a value
  if (operator === 'isEmpty' || operator === 'isNotEmpty') {
    return { operator }
  }

  // isAnyOf uses an array of values
  if (operator === 'isAnyOf') {
    const values = Array.isArray(item.value) ? item.value : [item.value]
    return { operator, values: values.filter(Boolean) }
  }

  // Other operators need a value
  if (!item.value && item.value !== 0) return null
  return { operator, value: String(item.value) }
}

function convertDateFilter(item: GridFilterItem): DateFilter | null {
  const operator = MUI_TO_BACKEND_DATE_OPERATORS[item.operator]
  if (!operator) return null

  // isEmpty/isNotEmpty don't need a value
  if (operator === 'isEmpty' || operator === 'isNotEmpty') {
    return { operator }
  }

  // Other operators need a value
  if (!item.value) return null

  // Convert Date object to ISO string if needed
  const value = item.value instanceof Date ? item.value.toISOString() : String(item.value)

  return { operator, value }
}

function convertNumberFilter(item: GridFilterItem): NumberFilter | null {
  const operator = MUI_TO_BACKEND_NUMBER_OPERATORS[item.operator]
  if (!operator) return null

  // isEmpty/isNotEmpty don't need a value
  if (operator === 'isEmpty' || operator === 'isNotEmpty') {
    return { operator }
  }

  // Accept both number and numeric string values
  const numValue = typeof item.value === 'number' ? item.value : Number(item.value)
  if (Number.isNaN(numValue)) return null

  return { operator, value: String(item.value) }
}

/**
 * Converts a boolean filter item to a string "true" or "false".
 * MUI DataGrid boolean columns use "is" operator with true/false value.
 */
function convertBooleanFilter(item: GridFilterItem): string | null {
  if (item.value === true || item.value === 'true') return 'true'
  if (item.value === false || item.value === 'false') return 'false'
  return null
}

/**
 * Converts MUI DataGrid filter items to backend filter parameters.
 *
 * Multiple rows on the same column map to the same backend param, so they are collected and sent as
 * a JSON array (which the backend ANDs) instead of overwriting each other.
 *
 * @param filterItems - Array of MUI DataGrid filter items
 * @param fieldMapping - Mapping of DataGrid fields to backend filter params
 * @returns Object with backend filter parameters (JSON-encoded strings)
 *
 * @example
 * const fieldMapping = {
 *   patient: { paramName: 'patientFilter', type: 'text' },
 *   email: { paramName: 'emailFilter', type: 'text' },
 *   lastContact: { paramName: 'createdAtFilter', type: 'date' },
 * };
 *
 * const filters = convertDataGridFilters(filterItems, fieldMapping);
 * // One row:  { patientFilter: '{"operator":"contains","value":"smith"}' }
 * // Two rows: { patientFilter: '[{"operator":"contains","value":"smith"},{"operator":"doesNotContain","value":"jr"}]' }
 */
export function convertDataGridFilters(
  filterItems: GridFilterItem[],
  fieldMapping: FilterFieldMapping
): Record<string, string> {
  const result: Record<string, string> = {}
  const jsonFiltersByParam = new Map<string, Array<TextFilter | DateFilter | NumberFilter>>()

  for (const item of filterItems) {
    const config = fieldMapping[item.field]
    if (!config) continue

    // Boolean filters are sent as plain "true" or "false" strings, never JSON.
    if (config.type === 'boolean') {
      const boolValue = convertBooleanFilter(item)
      if (boolValue) {
        result[config.paramName] = boolValue
      }
      continue
    }

    const filter =
      config.type === 'text'
        ? convertTextFilter(item)
        : config.type === 'date'
          ? convertDateFilter(item)
          : convertNumberFilter(item)
    if (!filter) continue

    const collected = jsonFiltersByParam.get(config.paramName)
    if (collected) collected.push(filter)
    else jsonFiltersByParam.set(config.paramName, [filter])
  }

  for (const [paramName, filters] of jsonFiltersByParam) {
    result[paramName] = JSON.stringify(filters.length === 1 ? filters[0] : filters)
  }

  return result
}

/**
 * Converts MUI DataGrid sort model to backend sort parameters.
 *
 * @param sortItems - Array of MUI DataGrid sort items
 * @param fieldMapping - Mapping of DataGrid fields to backend sort field names
 * @returns Object with sortBy and sortByDirection, or empty object if no sort
 *
 * @example
 * const sortMapping = {
 *   patient: 'patient',
 *   email: 'email',
 *   lastContact: 'created_at',
 * };
 *
 * const sort = convertDataGridSort([{ field: 'lastContact', sort: 'desc' }], sortMapping);
 * // Returns: { sortBy: 'created_at', sortByDirection: 'desc' }
 */
export function convertDataGridSort(
  sortItems: GridSortItem[],
  fieldMapping: SortFieldMapping
): { sortBy?: string; sortByDirection?: 'asc' | 'desc' } {
  if (!sortItems.length) return {}

  const item = sortItems[0] // Backend only supports single-field sorting
  const backendField = fieldMapping[item.field]

  if (!backendField || !item.sort) return {}

  return {
    sortBy: backendField,
    sortByDirection: item.sort
  }
}

/**
 * Serialize a hand-constructed text filter into the JSON string the backend expects.
 * Used when firing filter-backed queries outside the DataGrid UI flow (dashboard
 * search, RingCentral lookup, deep links).
 */
export function buildTextFilterParam(filter: {
  operator: BackendTextFilterOperator
  value?: string
  values?: string[]
}): string {
  return JSON.stringify(filter)
}

/**
 * Hook helper to create filter parameters from DataGrid state.
 * Combines search query, column filters, and sorting.
 *
 * @example
 * const params = buildFilterParams({
 *   searchQuery: 'john',
 *   filterItems: [...],
 *   sortItems: [...],
 *   filterFieldMapping: { patient: { paramName: 'patientFilter', type: 'text' } },
 *   sortFieldMapping: { patient: 'patient' },
 * });
 */
export function buildFilterParams(options: {
  searchQuery?: string
  filterItems?: GridFilterItem[]
  sortItems?: GridSortItem[]
  filterFieldMapping: FilterFieldMapping
  sortFieldMapping: SortFieldMapping
}): Record<string, string | undefined> {
  const { searchQuery, filterItems = [], sortItems = [], filterFieldMapping, sortFieldMapping } = options

  const columnFilters = convertDataGridFilters(filterItems, filterFieldMapping)
  const sortParams = convertDataGridSort(sortItems, sortFieldMapping)

  return {
    ...(searchQuery ? { searchQuery } : {}),
    ...columnFilters,
    ...sortParams
  }
}
